import React, { useState, useEffect, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp, ExternalLink, FileText } from 'lucide-react';
import { buildApiUrl } from '../config';
import { getPolicyTagIcon, getPolicyTagColor } from '../scripts/bills/policyTagUtils';

const SponsoredBills = ({ politician, isDarkMode, onNavigateToBill }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all | primary | co-sponsor
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBillId, setExpandedBillId] = useState(null);

  useEffect(() => {
    if (!politician?.id) return;
    fetchSponsored();
  }, [politician?.id]);

  const fetchSponsored = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(buildApiUrl(`/api/politicians/${encodeURIComponent(politician.id)}/sponsored-bills`));
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Error fetching sponsored bills:', err);
      setError('Failed to load sponsored legislation');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = useMemo(() => {
    return (records || [])
      .filter(r => {
        if (filter === 'primary') return r.sponsor_type === 'primary';
        if (filter === 'co-sponsor') return r.sponsor_type === 'co-sponsor';
        return true;
      })
      .filter(r => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();
        return (
          (r.bill_number || '').toLowerCase().includes(term) ||
          (r.title || '').toLowerCase().includes(term) ||
          (r.status_label || '').toLowerCase().includes(term)
        );
      });
  }, [records, filter, searchTerm]);

  const getSponsorLabel = (type) => {
    const t = (type || '').toLowerCase();
    if (t === 'primary') return 'Primary Sponsor';
    if (t === 'co-sponsor') return 'Co-sponsor';
    return 'Sponsor';
  };

  const getSponsorBadgeClass = (type) => {
    const t = (type || '').toLowerCase();
    if (t === 'primary') {
      return isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-600/60' : 'bg-green-100 text-green-800 border border-green-200';
    }
    if (t === 'co-sponsor') {
      return isDarkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-600/60' : 'bg-blue-100 text-blue-800 border border-blue-200';
    }
    return isDarkMode ? 'bg-gray-800 text-gray-200 border border-gray-600/60' : 'bg-gray-100 text-gray-800 border border-gray-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading sponsored bills...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className={isDarkMode ? 'text-red-400' : 'text-red-600'}>{error}</p>
      </div>
    );
  }

  if (!filteredRecords.length && !records.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <FileText className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
          <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} mb-2`}>No sponsored legislation found</h3>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
            This member is not currently listed as a primary sponsor or co-sponsor on any bills in our database.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Search and filter - same pattern as VotingRecords */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 md:p-6 border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="mb-2 md:mb-4">
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search bills by number or title..."
              className={`w-full pl-9 pr-3 py-2 text-sm md:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Sponsor type:</span>
          {[
            { value: 'all', label: 'All' },
            { value: 'primary', label: 'Primary' },
            { value: 'co-sponsor', label: 'Co-sponsored' }
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setFilter(opt.value)}
              className={`px-2.5 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                filter === opt.value
                  ? isDarkMode ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-600 text-white border-blue-600'
                  : isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-3 md:mt-4 pt-2.5 md:pt-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          Showing {filteredRecords.length} of {records.length} sponsored bills
        </div>
      </div>

      {/* Bill cards - layout aligned with VotingRecords */}
      <div className="space-y-2.5 md:space-y-3">
        {filteredRecords.map((rec) => {
          const tags = Array.isArray(rec.policy_tags) ? rec.policy_tags : [];
          const isExpanded = expandedBillId === rec.bill_id;
          return (
            <div
              key={rec.bill_id + rec.sponsor_type}
              onClick={() => setExpandedBillId(isExpanded ? null : rec.bill_id)}
              className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} overflow-hidden transition-all duration-200 hover:shadow-lg relative cursor-pointer`}
            >
              <div className="p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-start md:items-center md:space-x-4 flex-1">
                    <div className="hidden md:flex items-center space-x-2">
                      <span className={`px-2.5 md:px-3 py-0.5 md:py-1 rounded-full text-[11px] md:text-xs font-medium ${getSponsorBadgeClass(rec.sponsor_type)}`}>
                        {getSponsorLabel(rec.sponsor_type)}
                      </span>
                    </div>
                    <div className="flex-1 md:pl-0 pl-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 md:gap-3">
                        <span className={`font-bold text-sm md:text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {rec.bill_number}
                        </span>
                        {rec.status_label && (
                          <span className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {rec.status_label}
                          </span>
                        )}
                        <span className={`md:hidden inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${getSponsorBadgeClass(rec.sponsor_type)}`}>
                          {getSponsorLabel(rec.sponsor_type)}
                        </span>
                      </div>
                      <p className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mt-1 line-clamp-2`}>
                        {rec.title || `Bill ${rec.bill_number}`}
                      </p>
                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {tags.slice(0, 3).map((tag, tagIdx) => {
                            const IconComponent = getPolicyTagIcon(tag);
                            const tagColor = getPolicyTagColor(tag, isDarkMode);
                            return (
                              <span key={tagIdx} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${tagColor} shadow-sm`}>
                                <IconComponent className="w-3 h-3 mr-1" />
                                {tag}
                              </span>
                            );
                          })}
                          {tags.length > 3 && <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>+{tags.length - 3}</span>}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 self-start md:self-auto">
                    {onNavigateToBill && rec.bill_number && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigateToBill(rec.bill_number);
                          }}
                          className={`p-2 rounded-lg text-xs font-medium transition-all md:hidden ${isDarkMode
                            ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                          aria-label="View Bill"
                          title="View Bill"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigateToBill(rec.bill_number);
                          }}
                          className={`hidden md:flex px-3 py-1 rounded-lg text-xs font-medium transition-all ${isDarkMode
                            ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          } items-center space-x-1`}
                        >
                          <ExternalLink className="w-3 h-3" />
                          <span>View Bill</span>
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedBillId(isExpanded ? null : rec.bill_id);
                      }}
                      className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    >
                      {isExpanded ? (
                        <ChevronUp className={`w-4 h-4 md:w-5 md:h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                      ) : (
                        <ChevronDown className={`w-4 h-4 md:w-5 md:h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                      )}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="text-sm">
                      <span className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Summary:
                      </span>
                      <p className={`mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {rec.ai_summary || 'No summary found'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SponsoredBills;

